import React from "react"
import { navigate } from "gatsby"
import {
  Container,
  FeaturedSection,
  FeaturedImage,
  FeaturedContent,
  CategoryText,
  FeaturedTitle,
  FeaturedAuthor,
  ArticlesSection,
  ArticleCardWrapper,
  ButtonSection,
  HomepageTabbedButton,
  LocalArticleHeadline,
  LocalArticleByline,
  StyledArticleLink,
} from "./styles"

// Custom button component that handles common ContentfulButton functionality
const CustomHomepageTabbedButton = ({ component }) => {
  const handleClick = () => {
    if (!component) return

    // Handle different action types
    if (component.action === "Link to URL" && component.targetUrl) {
      if (component.targetUrl.match(/^\//)) {
        navigate(component.targetUrl)
      } else {
        window.open(component.targetUrl, "_blank")
      }
    } else if (
      component.action === "Link to Anchor" &&
      component.targetNavigationAnchor?.anchorId
    ) {
      const element = document.querySelector(`#${component.targetNavigationAnchor.anchorId}`)
      if (element) {
        element.scrollIntoView({ behavior: "smooth" })
      }
    } else if (component.action === "Send Email" && component.mailToAddress) {
      window.location.href = `mailto:${component.mailToAddress}`
    } else if (component.onClick) {
      component.onClick()
    }
  }

  const buttonText = component?.text || "More Articles"

  return <HomepageTabbedButton onClick={handleClick}>{buttonText}</HomepageTabbedButton>
}

const HomepageTabbed = ({ items, button, buttonPosition }) => {
  if (!items) {
    return null
  }

  const trimmedItems = items.slice(0, 7)

  const [firstItem, ...otherItems] = trimmedItems

  // Extract author name from nested structure
  const getAuthorName = (item) => {
    if (item?.article?.authors && item.article.authors.length > 0) {
      return item.article.authors[0].name
    }
    return item?.byline || null
  }

  // Get the background image URL
  const getFeaturedImage = (item) => {
    let previewImagePath = null
    if (item?.article?.previewImage) {
      try {
        const previewImageData = JSON.parse(item.article.previewImage)
        previewImagePath = previewImageData?.path
      } catch (e) {
        // Silently handle parsing errors
      }
    }

    return previewImagePath
  }

  const featuredImage = getFeaturedImage(firstItem)

  return (
    <>
      <Container>
        <FeaturedSection>
          <LinkToArticle
            url={firstItem?.article?.url}
            title={
              firstItem?.headlineCard ||
              firstItem?.article?.titles?.site ||
              firstItem?.article?.titles?.body ||
              "Article Title"
            }
          >
            <FeaturedImage
              baseUrl={featuredImage}
              alt={`Go to Article "${firstItem?.article?.titles?.site}"`}
              images={[
                {
                  transforms: "c_fill,g_auto,w_600,h_400,q_90,f_auto,fl_lossy",
                  screenSize: 1200,
                },
                {
                  transforms: "c_fill,g_auto,w_520,h_320,q_90,f_auto,fl_lossy",
                  screenSize: 992,
                },
                {
                  transforms: "c_fill,g_auto,w_440,h_280,q_90,f_auto,fl_lossy",
                  screenSize: 768,
                },
                {
                  transforms: "c_fill,g_auto,w_685,h_230,q_90,f_auto,fl_lossy",
                  screenSize: 576,
                },
                {
                  transforms: "c_fill,g_auto,w_540,h_210,q_90,f_auto,fl_lossy",
                  screenSize: 430,
                },
                {
                  transforms: "c_fill,g_auto,w_390,h_205,q_90,f_auto,fl_lossy",
                },
              ]}
              lazy
            />
          </LinkToArticle>
          <FeaturedContent>
            {firstItem?.article?.category && (
              <CategoryText>{firstItem.article.category}</CategoryText>
            )}
            <LinkToArticle
              url={firstItem?.article?.url}
              title={
                firstItem?.headlineCard ||
                firstItem?.article?.titles?.site ||
                firstItem?.article?.titles?.body ||
                "Article Title"
              }
            >
              <FeaturedTitle>
                {firstItem?.headlineCard ||
                  firstItem?.article?.titles?.site ||
                  firstItem?.article?.titles?.body ||
                  "Article Title"}
              </FeaturedTitle>
            </LinkToArticle>

            {getAuthorName(firstItem) && (
              <FeaturedAuthor>By {getAuthorName(firstItem)}</FeaturedAuthor>
            )}
          </FeaturedContent>
        </FeaturedSection>

        <ArticlesSection>
          {otherItems.map((item, index) => (
            <ArticleCardWrapper key={item.id}>
              <LinkToArticle
                url={item.article?.url}
              >
                <LocalArticleCard
                  title={
                    item.headlineCard || item.article?.titles?.site || item.article?.titles?.body
                  }
                  byline={getAuthorName(item)}
                />
              </LinkToArticle>
            </ArticleCardWrapper>
          ))}
        </ArticlesSection>
      </Container>
      {button && (
        <ButtonSection>
          <CustomHomepageTabbedButton component={button} />
        </ButtonSection>
      )}
    </>
  )
}

const LinkToArticle = ({ url, children, title }) => {
  if (!url) {
    return children
  }

  const linkUrl = url.includes("/articles/") ? url : `/articles/${url}`
  return (
    <StyledArticleLink to={linkUrl} {...(title && { title })}>
      {children}
    </StyledArticleLink>
  )
}

const LocalArticleCard = ({ title, byline, url }) => (
  <>
    <LocalArticleHeadline>{title}</LocalArticleHeadline>
    {byline && <LocalArticleByline>By {byline}</LocalArticleByline>}
  </>
)

export default HomepageTabbed
